/**
 * Invoice Payment Portal (Public Page)
 * Public-facing invoice view for recipients — no authentication required.
 * Displays branded invoice, line items, and allows payment recording.
 */

import React, { useState, useEffect } from 'react';
import {
  Download, DollarSign, CheckCircle, AlertTriangle, RefreshCw,
  FileText, CreditCard, Building, Banknote
} from 'lucide-react';
import './InvoicePrintStyles.css';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import {
  invoiceAPI,
  formatCents,
  centsToDecimal,
  decimalToCents,
  PaymentMethod,
  PAYMENT_METHOD_LABELS,
} from '@/services/api/invoiceAPI';

interface InvoicePaymentPortalProps {
  token: string;
}

export function InvoicePaymentPortal({ token }: InvoicePaymentPortalProps) {
  const { fmt } = useFormattedDate();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Payment form
  const [showPayment, setShowPayment] = useState(false);
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState<PaymentMethod>('check');
  const [payReference, setPayReference] = useState('');
  const [payNotes, setPayNotes] = useState('');
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await invoiceAPI.getPortalInvoice(token);
        setData(result);
      } catch (err: any) {
        if (err.response?.status === 404) {
          setError('This invoice link has expired or is no longer valid.');
        } else {
          setError('Unable to load invoice. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const handleDownloadPdf = async () => {
    try {
      const blob = await invoiceAPI.downloadPortalPdf(token);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice_${data.invoice.invoice_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download PDF');
    }
  };

  const handleRecordPayment = async () => {
    if (!payAmount) return;
    setPaying(true);
    setError(null);

    try {
      const result = await invoiceAPI.recordPortalPayment(token, {
        amount_cents: decimalToCents(parseFloat(payAmount)),
        payment_method: payMethod,
        reference_number: payReference || undefined,
        notes: payNotes || undefined,
      });

      setSuccess('Payment recorded successfully! Thank you.');
      setShowPayment(false);

      // Refresh data
      const updated = await invoiceAPI.getPortalInvoice(token);
      setData(updated);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to record payment');
    } finally {
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-3" />
          <p className="text-gray-500">Loading invoice...</p>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-gray-700 mb-2">Invoice Unavailable</h2>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  const inv = data.invoice;
  const branding = data.branding || {};
  const primaryColor = branding.primary_color || '#3B82F6';
  const companyName = branding.company_name || 'Organization';
  const isPaid = inv.status === 'paid';
  const isVoid = inv.status === 'void';

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-3xl mx-auto py-8 px-4">
        {/* Company Header */}
        <div className="text-center mb-6">
          {branding.logo_url && (
            <img src={branding.logo_url} alt={companyName} className="h-14 mx-auto mb-2" />
          )}
          <h1 className="text-xl font-bold text-gray-800">{companyName}</h1>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-4 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        )}
        {success && (
          <div className="mb-4 flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
            <span className="text-sm text-green-700">{success}</span>
          </div>
        )}

        {/* Invoice Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Header Bar */}
          <div className="px-8 py-5" style={{ borderTop: `4px solid ${primaryColor}` }}>
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-gray-500">Invoice</p>
                <h2 className="text-xl font-bold text-gray-900">{inv.invoice_number}</h2>
              </div>
              <div className="text-right">
                {isPaid ? (
                  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                    <CheckCircle className="w-4 h-4" /> PAID
                  </div>
                ) : isVoid ? (
                  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-600 rounded-full text-sm font-semibold">
                    VOID
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-gray-500">Amount Due</p>
                    <p className="text-2xl font-bold" style={{ color: primaryColor }}>
                      {formatCents(inv.amount_due_cents, inv.currency)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Details */}
          <div className="px-8 py-4 grid grid-cols-2 gap-4 bg-gray-50 border-t border-b border-gray-100">
            <div className="text-sm">
              <p className="text-gray-500">Invoice Date</p>
              <p className="font-medium text-gray-900">{inv.invoice_date ? fmt.long(inv.invoice_date) : '-'}</p>
            </div>
            <div className="text-sm">
              <p className="text-gray-500">Due Date</p>
              <p className="font-medium text-gray-900">{inv.due_date ? fmt.long(inv.due_date) : '-'}</p>
            </div>
            <div className="text-sm">
              <p className="text-gray-500">Bill To</p>
              <p className="font-medium text-gray-900">{inv.customer_snapshot?.name || '-'}</p>
              {inv.customer_snapshot?.company && <p className="text-gray-600">{inv.customer_snapshot.company}</p>}
            </div>
            <div className="text-sm">
              <p className="text-gray-500">Payment Terms</p>
              <p className="font-medium text-gray-900">{inv.payment_terms || 'Net 30'}</p>
            </div>
          </div>

          {/* Line Items */}
          <div className="px-8 py-4">
            <table className="w-full">
              <thead>
                <tr className="text-xs text-gray-500 uppercase tracking-wider border-b border-gray-200">
                  <th className="pb-2 text-left font-medium">Description</th>
                  <th className="pb-2 text-right font-medium w-16">Qty</th>
                  <th className="pb-2 text-right font-medium w-24">Price</th>
                  <th className="pb-2 text-right font-medium w-24">Amount</th>
                </tr>
              </thead>
              <tbody>
                {data.line_items.map((item: any, idx: number) => (
                  <tr key={idx} className="border-b border-gray-100 last:border-0">
                    <td className="py-3 text-sm text-gray-900">{item.description}</td>
                    <td className="py-3 text-sm text-gray-600 text-right">{item.quantity}</td>
                    <td className="py-3 text-sm text-gray-600 text-right">{formatCents(item.unit_price_cents)}</td>
                    <td className="py-3 text-sm text-gray-900 text-right font-medium">{formatCents(item.amount_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="px-8 py-4 border-t border-gray-200">
            <div className="flex justify-end">
              <div className="w-64 space-y-1.5">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Subtotal</span>
                  <span>{formatCents(inv.subtotal_cents)}</span>
                </div>
                {inv.discount_cents > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Discount</span>
                    <span className="text-red-600">-{formatCents(inv.discount_cents)}</span>
                  </div>
                )}
                {inv.tax_cents > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Tax</span>
                    <span>{formatCents(inv.tax_cents)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm font-bold pt-2 border-t border-gray-300">
                  <span>Total</span>
                  <span>{formatCents(inv.total_cents)}</span>
                </div>
                {inv.amount_paid_cents > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Amount Paid</span>
                    <span className="text-green-600">{formatCents(inv.amount_paid_cents)}</span>
                  </div>
                )}
                {!isPaid && inv.amount_due_cents > 0 && (
                  <div className="flex justify-between text-base font-bold pt-1" style={{ color: primaryColor }}>
                    <span>Balance Due</span>
                    <span>{formatCents(inv.amount_due_cents)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Payment Instructions */}
          {inv.payment_instructions && (
            <div className="px-8 py-4 bg-gray-50 border-t border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Payment Instructions</p>
              <p className="text-sm text-gray-700">{inv.payment_instructions}</p>
            </div>
          )}

          {/* Memo */}
          {inv.memo && (
            <div className="px-8 py-4 border-t border-gray-200">
              <p className="text-sm text-gray-600">{inv.memo}</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
          <button onClick={handleDownloadPdf}
            className="flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 shadow-sm">
            <Download className="w-4 h-4" /> Download PDF
          </button>

          {!isPaid && !isVoid && inv.amount_due_cents > 0 && (
            <button onClick={() => setShowPayment(!showPayment)}
              className="flex items-center gap-2 px-5 py-2.5 text-white rounded-lg text-sm font-medium shadow-sm hover:opacity-90"
              style={{ backgroundColor: primaryColor }}>
              <DollarSign className="w-4 h-4" /> Record Payment
            </button>
          )}
        </div>

        {/* Payment Form */}
        {showPayment && (
          <div className="mt-4 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Record a Payment</h3>
            <p className="text-xs text-gray-500 mb-4">
              If you&apos;ve made a payment via check, wire, or bank transfer, record it here.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Payment Amount ($) *</label>
                <input type="number" value={payAmount} onChange={e => setPayAmount(e.target.value)}
                  placeholder={centsToDecimal(inv.amount_due_cents).toFixed(2)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg" step="0.01" min="0.01" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Payment Method</label>
                <select value={payMethod} onChange={e => setPayMethod(e.target.value as PaymentMethod)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg">
                  <option value="check">Check</option>
                  <option value="wire">Wire Transfer</option>
                  <option value="ach">ACH</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Reference / Check Number</label>
                <input type="text" value={payReference} onChange={e => setPayReference(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg" placeholder="e.g., Check #1234" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Notes</label>
                <input type="text" value={payNotes} onChange={e => setPayNotes(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg" placeholder="Optional notes" />
              </div>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <button onClick={handleRecordPayment} disabled={paying || !payAmount}
                className="px-5 py-2 text-sm text-white rounded-lg disabled:opacity-50"
                style={{ backgroundColor: primaryColor }}>
                {paying ? 'Submitting...' : 'Submit Payment'}
              </button>
              <button onClick={() => setShowPayment(false)} className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700">
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-gray-400">
            Powered by WeaveIn.ai &middot; Invoice {inv.invoice_number}
          </p>
        </div>
      </div>
    </div>
  );
}
